/**
 * Frontend contract for the ai-agent-service chat-thread API.
 *
 * This MIRRORS the backend's zod contracts at
 * `services/ai-agent-service/src/contracts/chat.ts`. The agent backend is the
 * TypeScript ai-agent-service — NOT a PHP controller — so its responses are bare
 * JSON shapes, not the `{ success, dados, error }` envelope and not a
 * Spatie-generated contract under `src/contracts/*.ts`. The auto-generated
 * registry + httpService `responseContract` option therefore do not apply here,
 * and the frontend cannot import the agent package (it is not a workspace
 * dependency).
 *
 * Validation is a set of hand-written type guards parsed at the composable
 * boundary — the same runtime-validate-at-the-boundary posture the agent service
 * itself uses (hand-written/zod schema + a conformance test). A drifted/garbage
 * body is rejected by the relevant `parse*` function, turning backend drift into
 * a visible, typed error in the composable rather than corrupt UI. The
 * integration spec's NEGATIVE case proves the guards bite.
 *
 * No zod: nothing under `src/` uses zod (it is not a declared dependency); the
 * codebase's idiomatic boundary check is a hand-written guard (see
 * `src/contracts/validate.ts` and `useCep.ts`).
 *
 * SECURITY INVARIANT: the diagnostics guard accepts ONLY the PII-free fields
 * (tokens, per-tool name+outcome+latency, finishReason, requestId, iterations).
 * It must NOT surface raw tool args or backend response bodies even if a future
 * backend leaked them — diagnostics are PII-free by construction on both ends.
 */

// ─── Chat-thread API contracts (mirror src/contracts/chat.ts on the backend) ───

/** Message author role. */
export type ChatMessageRole = 'user' | 'assistant'

/** Outcome of a single tool invocation inside the agent loop. */
export type ToolOutcome = 'ok' | 'error'

/**
 * Per-tool diagnostic entry. PII-FREE BY CONSTRUCTION — tool NAME, outcome and
 * latency only. NEVER the tool arguments or the backend response body.
 */
export interface ToolDiagnostic {
  name: string
  outcome: ToolOutcome
  /** AgentError.code when `outcome === 'error'`; absent otherwise. */
  errorCode?: string
  latencyMs: number
}

/**
 * Diagnostics attached to an assistant turn. PII-FREE: tokens, per-tool
 * name+outcome+latency, finishReason, requestId, iterations, and (when billing
 * ran) raw micro-credit accounting figures ONLY.
 *
 * The three `*Micro` fields are absent (undefined) when billingFranquiaId is
 * null for the chat (billing skipped). When present they are integers or null.
 * These are the ONLY sanctioned raw-micro surface in the frontend — all other
 * surfaces must render humanized credits via formatCredits().
 */
export interface AgentDiagnostics {
  requestId: string
  iterations: number
  finishReason: string
  promptTokens: number | null
  completionTokens: number | null
  totalTokens: number | null
  tools: ToolDiagnostic[]
  /** Raw micro-credits consumed by this turn. Absent when billing was skipped. */
  creditsUsedMicro?: number | null
  /** Raw wallet balance (micros) before this turn. Absent when billing was skipped. */
  balanceBeforeMicro?: number | null
  /** Raw wallet balance (micros) after this turn. Absent when billing was skipped. */
  balanceAfterMicro?: number | null
}

/** A persisted message as returned by the API (DB read shape). */
export interface ChatMessageView {
  id: number
  chatId: number
  role: ChatMessageRole
  content: string
  /** ISO 8601 UTC. */
  createdAt: string
  /** Non-null for assistant turns; null for user turns. */
  diagnostics: AgentDiagnostics | null
}

/** A chat thread summary (no messages). */
export interface ChatSummary {
  id: number
  /** Cognito `sub` of the owner. */
  userId: string
  title: string
  tenant: string
  /** ISO 8601 UTC. */
  createdAt: string
  /** ISO 8601 UTC. */
  updatedAt: string
  /** ISO 8601 UTC — null when the chat is active. */
  archivedAt: string | null
  /** Franquia billing ID — null when not linked. */
  billingFranquiaId: number | null
}

/** A chat thread with its messages (GET /chats/:id). */
export interface ChatDetail extends ChatSummary {
  messages: ChatMessageView[]
}

// ─── AgentEvent — SSE streaming discriminated union ──────────────────────────
// SSOT mirror of the backend's AgentEventSchema (services/ai-agent-service/src/contracts/chat.ts).
// Each variant is emitted as a `data: <json>\n\n` frame on the SSE stream.

export interface AgentEventToken {
  type: 'token'
  text: string
}

/** Model reasoning trace, PII-free, ephemeral/stream-only (not persisted). */
export interface AgentEventReason {
  type: 'reason'
  text: string
}

export interface AgentEventToolStart {
  type: 'tool_start'
  name: string
}

export interface AgentEventToolResult {
  type: 'tool_result'
  name: string
  outcome: 'ok' | 'error'
  latencyMs: number
}

export interface AgentEventDone {
  type: 'done'
  messageId: number
  diagnostics: AgentDiagnostics
}

export interface AgentEventError {
  type: 'error'
  code: string
  message: string
}

/**
 * A client-side action request emitted by the agent (M0 wire schema).
 * `action` is treated as an opaque string here — allow-list validation is M8's job.
 * `payload` is intentionally `unknown` to stay minimal and forward-compatible.
 */
export interface AgentEventUiAction {
  type: 'ui_action'
  action: string
  payload: unknown
  id: string
}

export type AgentEvent =
  | AgentEventToken
  | AgentEventReason
  | AgentEventToolStart
  | AgentEventToolResult
  | AgentEventDone
  | AgentEventError
  | AgentEventUiAction

/**
 * Parse and validate a single SSE frame JSON string into an AgentEvent.
 * Throws a PT boundary error on unknown discriminant or malformed payload.
 */
export function parseAgentEvent(raw: unknown): AgentEvent {
  const obj = asRecord(raw, 'Evento do assistente')
  const type = obj['type']

  if (type === 'token') {
    return {
      type: 'token',
      text: requireString(obj, 'text', 'Evento token'),
    }
  }

  if (type === 'reason') {
    return {
      type: 'reason',
      text: requireString(obj, 'text', 'Evento reason'),
    }
  }

  if (type === 'tool_start') {
    return {
      type: 'tool_start',
      name: requireString(obj, 'name', 'Evento tool_start'),
    }
  }

  if (type === 'tool_result') {
    const outcome = obj['outcome']
    if (outcome !== 'ok' && outcome !== 'error') {
      throw new Error('Evento tool_result inválido: campo "outcome" inesperado')
    }
    const latencyMs = requireNumber(obj, 'latencyMs', 'Evento tool_result')
    if (!Number.isInteger(latencyMs) || latencyMs < 0) {
      throw new Error('Evento tool_result inválido: campo "latencyMs" deve ser inteiro não-negativo')
    }
    return {
      type: 'tool_result',
      name: requireString(obj, 'name', 'Evento tool_result'),
      outcome,
      latencyMs,
    }
  }

  if (type === 'done') {
    const messageId = requireNumber(obj, 'messageId', 'Evento done')
    if (!Number.isInteger(messageId) || messageId <= 0) {
      throw new Error('Evento done inválido: campo "messageId" deve ser inteiro positivo')
    }
    return {
      type: 'done',
      messageId,
      diagnostics: parseDiagnostics(obj['diagnostics']),
    }
  }

  if (type === 'error') {
    return {
      type: 'error',
      code: requireString(obj, 'code', 'Evento error'),
      message: requireString(obj, 'message', 'Evento error'),
    }
  }

  if (type === 'ui_action') {
    return {
      type: 'ui_action',
      // action is an opaque string — no allow-list at this layer (M8's job)
      action: requireString(obj, 'action', 'Evento ui_action'),
      payload: obj['payload'] ?? null,
      id: requireString(obj, 'id', 'Evento ui_action'),
    }
  }

  throw new Error(`Evento do assistente inválido: tipo desconhecido "${String(type)}"`)
}

/** A single tool's aggregate outcome counts (GET /admin/stats). */
export interface ToolOutcomeStat {
  name: string
  ok: number
  error: number
}

/** GET /admin/stats payload. Counts/latencies/outcomes ONLY — no content. */
export interface AgentStats {
  chatCount: number
  messageCount: number
  userCount: number
  avgAssistantLatencyMs: number | null
  toolOutcomes: ToolOutcomeStat[]
}

/** GET /health payload. */
export interface AgentHealth {
  status: 'ok'
  service: 'ai-agent-service'
}

/** GET /ready payload. */
export interface AgentReady {
  status: 'ready' | 'not_ready'
  db: boolean
}

/**
 * One currency's balance on the DeepSeek provider account (GET /provider/balance).
 * The three balance fields are decimal STRINGS exactly as returned by the
 * provider (e.g. '12.34') — never parsed/converted at this layer.
 */
export interface ProviderBalanceInfo {
  currency: string
  total_balance: string
  granted_balance: string
  topped_up_balance: string
}

/**
 * GET /provider/balance payload when the agent successfully reached DeepSeek.
 * `scope: 'provider_account'` marks this as the WHOLE provider account, shared
 * across environments — never attributable to a single tenant/franchise.
 * `balance_infos` may include non-USD currencies (e.g. CNY) with no conversion.
 */
export interface ProviderBalanceAvailable {
  available: true
  scope: 'provider_account'
  is_available: boolean
  balance_infos: ProviderBalanceInfo[]
}

/**
 * Fail-soft shape returned when the agent could not reach or parse the
 * upstream DeepSeek response. Always delivered as HTTP 200 — never a thrown
 * error — so the composable must not treat this as a transport failure.
 */
export interface ProviderBalanceUnavailable {
  available: false
  scope: 'provider_account'
  reason: string
}

export type ProviderBalance = ProviderBalanceAvailable | ProviderBalanceUnavailable

// ─── Guards ──────────────────────────────────────────────────────────────────
// Each guard rejects a missing/retyped field with a PT error so backend drift
// surfaces as a typed, visible failure in the composable (never corrupt UI).

function asRecord(raw: unknown, label: string): Record<string, unknown> {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    throw new Error(`${label} inválido: corpo ausente ou de tipo inesperado`)
  }
  return raw as Record<string, unknown>
}

function requireString(obj: Record<string, unknown>, key: string, label: string): string {
  const value = obj[key]
  if (typeof value !== 'string') {
    throw new Error(`${label} inválido: campo "${key}" ausente ou de tipo inesperado`)
  }
  return value
}

function requireNumber(obj: Record<string, unknown>, key: string, label: string): number {
  const value = obj[key]
  if (typeof value !== 'number' || Number.isNaN(value)) {
    throw new Error(`${label} inválido: campo "${key}" ausente ou de tipo inesperado`)
  }
  return value
}

function requireNullableString(
  obj: Record<string, unknown>,
  key: string,
  label: string,
): string | null {
  const value = obj[key]
  if (value === null || value === undefined) {
    return null
  }
  if (typeof value !== 'string') {
    throw new Error(`${label} inválido: campo "${key}" deve ser string ou nulo`)
  }
  return value
}

function requireNullableNumber(
  obj: Record<string, unknown>,
  key: string,
  label: string,
): number | null {
  const value = obj[key]
  if (value === null) {
    return null
  }
  if (typeof value !== 'number' || Number.isNaN(value)) {
    throw new Error(`${label} inválido: campo "${key}" deve ser número ou nulo`)
  }
  return value
}

function requireBoolean(obj: Record<string, unknown>, key: string, label: string): boolean {
  const value = obj[key]
  if (typeof value !== 'boolean') {
    throw new Error(`${label} inválido: campo "${key}" ausente ou de tipo inesperado`)
  }
  return value
}

function requireArray(obj: Record<string, unknown>, key: string, label: string): unknown[] {
  const value = obj[key]
  if (!Array.isArray(value)) {
    throw new Error(`${label} inválido: campo "${key}" deve ser uma lista`)
  }
  return value
}

function requireRole(obj: Record<string, unknown>, label: string): ChatMessageRole {
  const value = obj.role
  if (value !== 'user' && value !== 'assistant') {
    throw new Error(`${label} inválido: campo "role" inesperado`)
  }
  return value
}

function parseToolDiagnostic(raw: unknown): ToolDiagnostic {
  const obj = asRecord(raw, 'Diagnóstico de ferramenta')
  const outcome = obj.outcome
  if (outcome !== 'ok' && outcome !== 'error') {
    throw new Error('Diagnóstico de ferramenta inválido: campo "outcome" inesperado')
  }
  const tool: ToolDiagnostic = {
    name: requireString(obj, 'name', 'Diagnóstico de ferramenta'),
    outcome,
    latencyMs: requireNumber(obj, 'latencyMs', 'Diagnóstico de ferramenta'),
  }
  // errorCode is optional; accept only a string when present.
  if (obj.errorCode !== undefined) {
    if (typeof obj.errorCode !== 'string') {
      throw new Error('Diagnóstico de ferramenta inválido: campo "errorCode" de tipo inesperado')
    }
    tool.errorCode = obj.errorCode
  }
  return tool
}

/**
 * Validate diagnostics. Builds the result from ONLY the PII-free fields — any
 * extra keys (e.g. a leaked `args` or response body) are dropped, never copied
 * into the returned object.
 */
export function parseDiagnostics(raw: unknown): AgentDiagnostics {
  const obj = asRecord(raw, 'Diagnóstico do assistente')
  const result: AgentDiagnostics = {
    requestId: requireString(obj, 'requestId', 'Diagnóstico do assistente'),
    iterations: requireNumber(obj, 'iterations', 'Diagnóstico do assistente'),
    finishReason: requireString(obj, 'finishReason', 'Diagnóstico do assistente'),
    promptTokens: requireNullableNumber(obj, 'promptTokens', 'Diagnóstico do assistente'),
    completionTokens: requireNullableNumber(obj, 'completionTokens', 'Diagnóstico do assistente'),
    totalTokens: requireNullableNumber(obj, 'totalTokens', 'Diagnóstico do assistente'),
    tools: requireArray(obj, 'tools', 'Diagnóstico do assistente').map(parseToolDiagnostic),
  }
  // Optional nullable micro-credit accounting fields — present only when billing ran.
  // Each must be a number or null (never a string or object).
  if ('creditsUsedMicro' in obj) {
    result.creditsUsedMicro = requireNullableNumber(
      obj,
      'creditsUsedMicro',
      'Diagnóstico do assistente',
    )
  }
  if ('balanceBeforeMicro' in obj) {
    result.balanceBeforeMicro = requireNullableNumber(
      obj,
      'balanceBeforeMicro',
      'Diagnóstico do assistente',
    )
  }
  if ('balanceAfterMicro' in obj) {
    result.balanceAfterMicro = requireNullableNumber(
      obj,
      'balanceAfterMicro',
      'Diagnóstico do assistente',
    )
  }
  return result
}

/** Validate a single persisted message. `diagnostics` is object-or-null. */
export function parseMessageView(raw: unknown): ChatMessageView {
  const obj = asRecord(raw, 'Mensagem do assistente')
  const diagnosticsRaw = obj.diagnostics
  const diagnostics =
    diagnosticsRaw === null || diagnosticsRaw === undefined
      ? null
      : parseDiagnostics(diagnosticsRaw)
  return {
    id: requireNumber(obj, 'id', 'Mensagem do assistente'),
    chatId: requireNumber(obj, 'chatId', 'Mensagem do assistente'),
    role: requireRole(obj, 'Mensagem do assistente'),
    content: requireString(obj, 'content', 'Mensagem do assistente'),
    createdAt: requireString(obj, 'createdAt', 'Mensagem do assistente'),
    diagnostics,
  }
}

/** Validate a chat summary. */
export function parseChat(raw: unknown): ChatSummary {
  const obj = asRecord(raw, 'Conversa')
  return {
    id: requireNumber(obj, 'id', 'Conversa'),
    userId: requireString(obj, 'userId', 'Conversa'),
    title: requireString(obj, 'title', 'Conversa'),
    tenant: requireString(obj, 'tenant', 'Conversa'),
    createdAt: requireString(obj, 'createdAt', 'Conversa'),
    updatedAt: requireString(obj, 'updatedAt', 'Conversa'),
    archivedAt: requireNullableString(obj, 'archivedAt', 'Conversa'),
    billingFranquiaId: requireNullableNumber(obj, 'billingFranquiaId', 'Conversa'),
  }
}

/** Validate the `{ chats: [...] }` list response. */
export function parseChatList(raw: unknown): ChatSummary[] {
  const obj = asRecord(raw, 'Lista de conversas')
  return requireArray(obj, 'chats', 'Lista de conversas').map(parseChat)
}

/** Validate a chat with its messages (GET /chats/:id). */
export function parseChatDetail(raw: unknown): ChatDetail {
  const summary = parseChat(raw)
  const obj = raw as Record<string, unknown>
  const messages = requireArray(obj, 'messages', 'Conversa').map(parseMessageView)
  return { ...summary, messages }
}

/** Validate the GET /admin/stats response. */
export function parseStats(raw: unknown): AgentStats {
  const obj = asRecord(raw, 'Estatísticas do assistente')
  const toolOutcomes = requireArray(obj, 'toolOutcomes', 'Estatísticas do assistente').map(
    (entry): ToolOutcomeStat => {
      const e = asRecord(entry, 'Estatística de ferramenta')
      return {
        name: requireString(e, 'name', 'Estatística de ferramenta'),
        ok: requireNumber(e, 'ok', 'Estatística de ferramenta'),
        error: requireNumber(e, 'error', 'Estatística de ferramenta'),
      }
    },
  )
  return {
    chatCount: requireNumber(obj, 'chatCount', 'Estatísticas do assistente'),
    messageCount: requireNumber(obj, 'messageCount', 'Estatísticas do assistente'),
    userCount: requireNumber(obj, 'userCount', 'Estatísticas do assistente'),
    avgAssistantLatencyMs: requireNullableNumber(
      obj,
      'avgAssistantLatencyMs',
      'Estatísticas do assistente',
    ),
    toolOutcomes,
  }
}

/** Validate the GET /health response. */
export function parseHealth(raw: unknown): AgentHealth {
  const obj = asRecord(raw, 'Status do assistente')
  const status = obj.status
  const service = obj.service
  if (status !== 'ok') {
    throw new Error('Status do assistente inválido: campo "status" inesperado')
  }
  if (service !== 'ai-agent-service') {
    throw new Error('Status do assistente inválido: campo "service" inesperado')
  }
  return { status, service }
}

/** Validate the GET /ready response. */
export function parseReady(raw: unknown): AgentReady {
  const obj = asRecord(raw, 'Prontidão do assistente')
  const status = obj.status
  if (status !== 'ready' && status !== 'not_ready') {
    throw new Error('Prontidão do assistente inválida: campo "status" inesperado')
  }
  return {
    status,
    db: requireBoolean(obj, 'db', 'Prontidão do assistente'),
  }
}

function parseProviderBalanceInfo(raw: unknown): ProviderBalanceInfo {
  const obj = asRecord(raw, 'Saldo do provedor por moeda')
  return {
    currency: requireString(obj, 'currency', 'Saldo do provedor por moeda'),
    total_balance: requireString(obj, 'total_balance', 'Saldo do provedor por moeda'),
    granted_balance: requireString(obj, 'granted_balance', 'Saldo do provedor por moeda'),
    topped_up_balance: requireString(obj, 'topped_up_balance', 'Saldo do provedor por moeda'),
  }
}

/**
 * Validate the GET /provider/balance response — a discriminated union on
 * `available`. `scope` must always read 'provider_account' (the only scope the
 * endpoint ever returns); any other value is treated as drift, not silently
 * accepted, since a future per-tenant scope would change what this tile means.
 */
export function parseProviderBalance(raw: unknown): ProviderBalance {
  const obj = asRecord(raw, 'Saldo do provedor')
  const available = obj.available
  if (available !== true && available !== false) {
    throw new Error('Saldo do provedor inválido: campo "available" ausente ou de tipo inesperado')
  }
  const scope = obj.scope
  if (scope !== 'provider_account') {
    throw new Error('Saldo do provedor inválido: campo "scope" inesperado')
  }
  if (available === false) {
    return {
      available: false,
      scope: 'provider_account',
      reason: requireString(obj, 'reason', 'Saldo do provedor'),
    }
  }
  return {
    available: true,
    scope: 'provider_account',
    is_available: requireBoolean(obj, 'is_available', 'Saldo do provedor'),
    balance_infos: requireArray(obj, 'balance_infos', 'Saldo do provedor').map(
      parseProviderBalanceInfo,
    ),
  }
}

// ---------------------------------------------------------------------------
// Sugestão de resposta de chamado (POST /chamados/:id/sugestao)
// ---------------------------------------------------------------------------

/** As intenções que o agente reconhece. Lista FECHADA — espelha o backend. */
export const INTENCOES_DE_CHAMADO = [
  'duvida',
  'bug',
  'liberacao',
  'cobranca',
  'melhoria',
  'outro',
] as const

export type IntencaoDeChamado = (typeof INTENCOES_DE_CHAMADO)[number]

export interface AnaliseDeChamado {
  readonly intencao: IntencaoDeChamado
  /** O que a pessoa quer, numa frase. */
  readonly resumo: string
  /** O que falta para resolver. Vazia é resultado legítimo. */
  readonly falta: readonly string[]
  readonly confianca: 'alta' | 'media' | 'baixa'
}

export interface SugestaoDeResposta {
  /** O rascunho, em texto puro. Nunca vazio — vazio é erro, não sugestão. */
  readonly sugestao: string
  /**
   * A leitura do chamado. AUSENTE é um estado válido: um agente mais antigo
   * (ou um deploy no meio) devolve só o rascunho, e a tela precisa continuar
   * funcionando sem o painel de análise em vez de quebrar.
   */
  readonly analise?: AnaliseDeChamado
  /** O agente afirma que o prompt ignorou as notas internas. */
  readonly ignorouNotasInternas: boolean
  /** Micros debitados, quando o settle respondeu. Ausente não é zero. */
  readonly creditsUsedMicro?: number
}

/**
 * Valida a resposta da sugestão.
 *
 * O ai-agent-service é outro serviço (TS/zod) e não passa pelo registro
 * PHP→Spatie, então a garantia de forma tem de ser escrita aqui — é a mesma
 * razão das outras entradas deste arquivo.
 *
 * `sugestao` vazia é REJEITADA: um campo vazio chegaria à tela como um
 * rascunho em branco, e o atendente concluiria que a IA "não achou nada" em
 * vez de saber que a chamada falhou.
 */
export function parseSugestaoDeResposta(raw: unknown): SugestaoDeResposta {
  const obj = asRecord(raw, 'Sugestão de resposta')
  const sugestao = requireString(obj, 'sugestao', 'Sugestão de resposta').trim()

  if (sugestao === '') {
    throw new Error('Sugestão de resposta inválida: campo "sugestao" veio vazio')
  }

  const creditos = obj.creditsUsedMicro
  const analise = lerAnalise(obj.analise)

  return {
    sugestao,
    ...(analise !== undefined ? { analise } : {}),
    // Ausente vira `false`, nunca `true`: na dúvida a tela AVISA que pode ter
    // visto nota interna, em vez de garantir que não viu. Errar para o lado
    // do aviso é barulho; errar para o outro é uma garantia falsa.
    ignorouNotasInternas: obj.ignorouNotasInternas === true,
    ...(typeof creditos === 'number' ? { creditsUsedMicro: creditos } : {}),
  }
}

/**
 * A análise, quando vem.
 *
 * Devolve `undefined` — e NÃO lança — quando o campo está ausente ou fora de
 * forma. A razão é a assimetria: sem análise a tela mostra só o rascunho, que
 * é exatamente o que ela fazia antes e continua útil. Derrubar a sugestão
 * inteira por causa de um painel auxiliar seria deixar o atendente sem nada
 * por causa do acessório.
 *
 * O backend já REJEITA análise malformada antes de responder; esta segunda
 * checagem existe para o caso de as duas pontas estarem em versões
 * diferentes — que é o estado normal durante um deploy.
 */
function lerAnalise(raw: unknown): AnaliseDeChamado | undefined {
  if (raw === null || typeof raw !== 'object') {
    return undefined
  }

  const obj = raw as Record<string, unknown>
  const intencao = obj.intencao
  const confianca = obj.confianca
  const resumo = obj.resumo

  if (typeof intencao !== 'string' || !(INTENCOES_DE_CHAMADO as readonly string[]).includes(intencao)) {
    return undefined
  }

  if (confianca !== 'alta' && confianca !== 'media' && confianca !== 'baixa') {
    return undefined
  }

  if (typeof resumo !== 'string' || resumo.trim() === '') {
    return undefined
  }

  return {
    intencao: intencao as IntencaoDeChamado,
    resumo: resumo.trim(),
    // `falta` ausente é lista vazia: "não falta nada" é resultado comum.
    falta: Array.isArray(obj.falta) ? obj.falta.filter((f): f is string => typeof f === 'string') : [],
    confianca,
  }
}

// ---------------------------------------------------------------------------
// Propostas da IA para a minuta de contrato (POST /contratos/minutas/:id/propostas)
// ---------------------------------------------------------------------------

export type AcaoDaProposta = 'alterar' | 'incluir' | 'remover'

export interface PropostaDaMinuta {
  readonly acao: AcaoDaProposta
  /** A cláusula alterada/removida, ou a chave NOVA de uma incluída. */
  readonly chave: string
  /** Só em `incluir`: depois de qual cláusula entra (null = no fim). */
  readonly depois_de: string | null
  readonly titulo: string
  readonly texto: string
  readonly motivo: string
}

export interface PropostasDaMinuta {
  readonly propostas: readonly PropostaDaMinuta[]
  /** O que a IA tentou e o agente recusou (cláusula fixa, campo inexistente…), com o motivo. */
  readonly descartadas: readonly (PropostaDaMinuta & { readonly porque: string })[]
  readonly observacao: string | null
}

const ACOES_DA_PROPOSTA: readonly string[] = ['alterar', 'incluir', 'remover']

function lerProposta(raw: unknown): PropostaDaMinuta {
  const obj = asRecord(raw, 'Proposta da minuta')
  const acao = requireString(obj, 'acao', 'Proposta da minuta')
  if (!ACOES_DA_PROPOSTA.includes(acao)) {
    throw new Error(`Proposta da minuta inválida: ação "${acao}"`)
  }
  const depois = obj.depois_de
  return {
    acao: acao as AcaoDaProposta,
    chave: requireString(obj, 'chave', 'Proposta da minuta'),
    depois_de: typeof depois === 'string' && depois !== '' ? depois : null,
    titulo: typeof obj.titulo === 'string' ? obj.titulo : '',
    texto: typeof obj.texto === 'string' ? obj.texto : '',
    motivo: typeof obj.motivo === 'string' ? obj.motivo : '',
  }
}

/**
 * Valida a resposta das propostas. O agente já descartou o que fere as regras; esta checagem é
 * de forma (duas pontas em versões diferentes durante um deploy). Uma proposta sem forma
 * derruba a resposta inteira: mostrar metade das mudanças faria a pessoa aprovar um contrato
 * achando que viu tudo o que a IA sugeriu.
 */
export function parsePropostasDaMinuta(raw: unknown): PropostasDaMinuta {
  const obj = asRecord(raw, 'Propostas da minuta')
  const descartadas = Array.isArray(obj.descartadas) ? obj.descartadas : []
  return {
    propostas: requireArray(obj, 'propostas', 'Propostas da minuta').map(lerProposta),
    descartadas: descartadas.map((d) => {
      const porque = asRecord(d, 'Proposta descartada').porque
      return { ...lerProposta(d), porque: typeof porque === 'string' ? porque : '' }
    }),
    observacao: typeof obj.observacao === 'string' && obj.observacao.trim() !== '' ? obj.observacao : null,
  }
}
